import React, { useCallback, useEffect, useState } from 'react';
import { api, ApiError, downloadFile, mutate } from '../shared/api';
import { formatDate, formatNumber } from '../shared/formatting';

type Locale = 'hr' | 'en';
type Dict = Record<string, string>;

type Supplier = { id: number; name: string; kind: 'goods' | 'carrier'; is_active: boolean; default_credit_limit_eur: number | null; default_days_to_pay: number; notes: string | null };
type PgRun = { id: number; public_code: string; mode: string; generated_at: string | null; line_count: number; total_eur: number; suppliers: string[] };
type Meta = {
    suppliers: Supplier[];
    warehouses: { code: string; name: string }[];
    plans: { id: number; plan_number: string; status: string }[];
    pg_runs: PgRun[];
    statuses: Dict;
    payment_terms: Dict;
    invoice_kinds: Dict;
    payment_states: Dict;
    ageing: Dict;
    arrival_methods: Dict;
    purposes: string[];
    can_edit: boolean;
    can_admin: boolean;
    inbound_available: boolean;
};

type OrderRow = {
    id: number; public_code: string; supplier_id: number; supplier_name: string; order_date: string; expected_date: string | null;
    status: string; warehouse_code: string | null; delivery_plan_id: number | null; pg_run_id: number | null; offer_no: string | null;
    total_eur: number; open_eur: number; invoiced_eur: number; invoice_count: number; partial: number; overdue: number; note: string | null; source: string;
};
type OrderLine = { id?: number; sku: string | null; item_name: string; qty: number; qty_received?: number; unit_price_eur: number; fine_au_g: number | null };
type InvoiceRow = {
    id: number; supplier_id: number; supplier_name: string; kind: string; invoice_no: string | null; invoice_date: string | null; due_date: string | null;
    amount_eur: number; paid_eur: number; paid_at: string | null; open_eur: number; unallocated_eur: number; allocated_orders: number;
    payment_state: string; ageing: string | null; reference_text: string | null; flags: string | null; source: string; goods_arrived: number | boolean | null;
    fine_au_g: number | null; note: string | null;
};

const n = (v: unknown) => Number(v ?? 0);
const today = () => new Date().toISOString().slice(0, 10);

const th: React.CSSProperties = { textAlign: 'left', padding: '0.35rem 0.5rem', borderBottom: '1px solid #999', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { padding: '0.3rem 0.5rem', borderBottom: '1px solid #ddd', verticalAlign: 'top' };
const num: React.CSSProperties = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };
const thn: React.CSSProperties = { ...th, textAlign: 'right' };
const table: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' };
const box: React.CSSProperties = { border: '1px solid #999', padding: '0.8rem', marginTop: '0.8rem' };
const row: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'flex-end', margin: '0.4rem 0' };

type Ctx = { locale: Locale; token: string | null; meta: Meta; t: (hr: string, en: string) => string; eur: (v: unknown) => string; date: (v: string | null) => string; onError: (e: unknown) => void; notify: (s: string) => void };

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.8rem', gap: 2 }}>
            <span className="muted">{label}</span>
            {children}
        </label>
    );
}

function qs(f: Record<string, string | boolean>) {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => {
        if (v === true) p.set(k, '1');
        else if (typeof v === 'string' && v !== '') p.set(k, v);
    });
    const s = p.toString();
    return s ? `?${s}` : '';
}

type Tab = 'pregled' | 'narudzbe' | 'racuni' | 'dolasci' | 'prijevoz' | 'pregled_uvoza' | 'dobavljaci';

/** Modul 40.45, Ino narudžbe i računi dobavljača. */
export function ImportOrdersModule({ locale, token, onBack }: { locale: Locale; token: string | null; onBack?: () => void }) {
    const t = useCallback((hr: string, en: string) => (locale === 'en' ? en : hr), [locale]);
    const [meta, setMeta] = useState<Meta | null>(null);
    const [tab, setTab] = useState<Tab>('pregled');
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);

    const loadMeta = useCallback(() => {
        api<Meta>('/import-orders/meta', token)
            .then(setMeta)
            .catch((e) => setError(e instanceof ApiError ? e.message : t('Učitavanje nije uspjelo.', 'Loading failed.')));
    }, [token, t]);
    useEffect(() => loadMeta(), [loadMeta]);

    const tabs: { key: Tab; hr: string; en: string }[] = [
        { key: 'pregled', hr: 'Pregled po dobavljaču', en: 'By supplier' },
        { key: 'narudzbe', hr: 'Narudžbe', en: 'Orders' },
        { key: 'racuni', hr: 'Računi', en: 'Invoices' },
        { key: 'dolasci', hr: 'Dolasci', en: 'Arrivals' },
        { key: 'prijevoz', hr: 'Prijevoz', en: 'Transport' },
        { key: 'pregled_uvoza', hr: 'Za pregled', en: 'Review' },
        { key: 'dobavljaci', hr: 'Dobavljači', en: 'Suppliers' },
    ];

    const ctx: Ctx | null = meta
        ? {
              locale,
              token,
              meta,
              t,
              eur: (v) => `${formatNumber(Math.round(n(v) * 100) / 100, locale)} €`,
              date: (v) => (v ? formatDate(v, locale) : ''),
              onError: (e) => setError(e instanceof ApiError ? e.message : t('Spremanje nije uspjelo.', 'Saving failed.')),
              notify: (s) => {
                  setError(null);
                  setNotice(s);
              },
          }
        : null;

    const exportXlsx = async () => {
        try {
            await downloadFile('/import-orders/export.xlsx', token, `ino-narudzbe-racuni-${today()}.xlsx`);
        } catch {
            setError(t('Izvoz nije uspio.', 'Export failed.'));
        }
    };

    return (
        <div className="module-shell">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem', flexWrap: 'wrap' }}>
                <div>
                    {onBack ? (
                        <button type="button" className="secondary compact" onClick={onBack}>
                            ← {t('Skladište', 'Warehouse')}
                        </button>
                    ) : null}
                    <p className="eyebrow">40.45</p>
                    <h2 style={{ margin: '0.2rem 0' }}>{t('Ino narudžbe i računi dobavljača', 'Foreign supplier orders and invoices')}</h2>
                    <p className="muted" style={{ margin: 0 }}>
                        {t(
                            'Narudžbe se zrcale u registar 40.4 i ondje su samo za čitanje. Računi s dospijećem ulaze u plan isplate, a potvrda dolaska zatvara alokacije vezanog plana.',
                            'Orders are mirrored read-only into register 40.4. Invoices with a due date enter the payment schedule, and arrival confirmation closes the linked plan allocations.',
                        )}
                    </p>
                </div>
                <button type="button" className="secondary compact" onClick={exportXlsx}>
                    {t('Izvoz u Excel', 'Export to Excel')}
                </button>
            </div>
            <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', margin: '0.8rem 0' }}>
                {tabs.map((x) => (
                    <button key={x.key} type="button" className={tab === x.key ? 'compact' : 'secondary compact'} onClick={() => setTab(x.key)}>
                        {locale === 'en' ? x.en : x.hr}
                    </button>
                ))}
            </div>
            {error ? (
                <p role="alert" style={{ color: '#a00' }}>
                    {error}
                </p>
            ) : null}
            {notice ? (
                <p role="status" style={{ border: '1px solid #999', padding: '0.5rem 0.8rem' }}>
                    {notice}
                </p>
            ) : null}
            {!ctx ? <p className="muted">{t('Učitavam...', 'Loading...')}</p> : null}
            {ctx && tab === 'pregled' ? <SummaryTab c={ctx} /> : null}
            {ctx && tab === 'narudzbe' ? <OrdersTab c={ctx} reloadMeta={loadMeta} /> : null}
            {ctx && tab === 'racuni' ? <InvoicesTab c={ctx} /> : null}
            {ctx && tab === 'dolasci' ? <ArrivalsTab c={ctx} /> : null}
            {ctx && tab === 'prijevoz' ? <TransportTab c={ctx} /> : null}
            {ctx && tab === 'pregled_uvoza' ? <ReviewTab c={ctx} /> : null}
            {ctx && tab === 'dobavljaci' ? <SuppliersTab c={ctx} reloadMeta={loadMeta} /> : null}
        </div>
    );
}

type SummaryRow = {
    supplier_id: number; supplier_name: string; open_orders: number; open_orders_eur: number; open_articles: { sku: string | null; item_name: string; qty: number; eur: number }[];
    unpaid_eur: number; unpaid_count: number; overdue_eur: number; paid_not_arrived_eur: number; credit_limit_eur: number | null; credit_used_eur: number; credit_used_pct: number | null;
};

function SummaryTab({ c }: { c: Ctx }) {
    const { t, eur } = c;
    const [data, setData] = useState<{ suppliers: SummaryRow[]; totals: Record<string, number> } | null>(null);
    const [open, setOpen] = useState<number | null>(null);
    useEffect(() => {
        api<{ suppliers: SummaryRow[]; totals: Record<string, number> }>('/import-orders/summary', c.token).then(setData).catch(c.onError);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [c.token]);
    if (!data) return <p className="muted">{t('Učitavam...', 'Loading...')}</p>;
    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={table}>
                <thead>
                    <tr>
                        <th style={th}>{t('Dobavljač', 'Supplier')}</th>
                        <th style={thn}>{t('Otvorene narudžbe', 'Open orders')}</th>
                        <th style={thn}>{t('Otvoreno EUR', 'Open EUR')}</th>
                        <th style={thn}>{t('Neplaćeno', 'Unpaid')}</th>
                        <th style={thn}>{t('Od toga dospjelo', 'Of which overdue')}</th>
                        <th style={thn}>{t('Plaćeno, nije stiglo', 'Paid, not arrived')}</th>
                        <th style={thn}>{t('Kreditni limit', 'Credit limit')}</th>
                        <th style={thn}>{t('Iskorišteno', 'Used')}</th>
                    </tr>
                </thead>
                <tbody>
                    {data.suppliers.map((s) => (
                        <React.Fragment key={s.supplier_id}>
                            <tr style={{ cursor: 'pointer' }} onClick={() => setOpen(open === s.supplier_id ? null : s.supplier_id)}>
                                <td style={td}>{s.supplier_name}</td>
                                <td style={num}>{s.open_orders}</td>
                                <td style={num}>{eur(s.open_orders_eur)}</td>
                                <td style={num}>{eur(s.unpaid_eur)}</td>
                                <td style={num}>{eur(s.overdue_eur)}</td>
                                <td style={num}>{eur(s.paid_not_arrived_eur)}</td>
                                <td style={num}>{s.credit_limit_eur !== null ? eur(s.credit_limit_eur) : '-'}</td>
                                <td style={num}>{s.credit_used_pct !== null ? `${formatNumber(s.credit_used_pct, c.locale)} %` : eur(s.credit_used_eur)}</td>
                            </tr>
                            {open === s.supplier_id && s.open_articles.length > 0 ? (
                                <tr>
                                    <td style={td} colSpan={8}>
                                        {s.open_articles.map((a, i) => (
                                            <div key={i} style={{ fontSize: '0.8rem' }}>
                                                {a.sku ?? '-'} {a.item_name}, {formatNumber(a.qty, c.locale)} {t('kom', 'pcs')}, {eur(a.eur)}
                                            </div>
                                        ))}
                                    </td>
                                </tr>
                            ) : null}
                        </React.Fragment>
                    ))}
                    <tr>
                        <td style={{ ...td, fontWeight: 700 }}>{t('Ukupno', 'Total')}</td>
                        <td style={num} />
                        <td style={{ ...num, fontWeight: 700 }}>{eur(data.totals.open_orders_eur)}</td>
                        <td style={{ ...num, fontWeight: 700 }}>{eur(data.totals.unpaid_eur)}</td>
                        <td style={{ ...num, fontWeight: 700 }}>{eur(data.totals.overdue_eur)}</td>
                        <td style={{ ...num, fontWeight: 700 }}>{eur(data.totals.paid_not_arrived_eur)}</td>
                        <td style={num} />
                        <td style={num} />
                    </tr>
                </tbody>
            </table>
            {data.suppliers.length === 0 ? <p className="muted">{t('Još nema narudžbi ni računa.', 'No orders or invoices yet.')}</p> : null}
        </div>
    );
}

type OrderForm = {
    id?: number; supplier_id: string; offer_no: string; order_date: string; expected_date: string; warehouse_code: string; delivery_plan_id: string;
    payment_terms: string; purpose: string; company: string; note: string; lines: OrderLine[]; deleted_line_ids: number[];
};
const emptyOrder = (): OrderForm => ({
    supplier_id: '', offer_no: '', order_date: today(), expected_date: '', warehouse_code: '', delivery_plan_id: '', payment_terms: '', purpose: 'regular', company: '', note: '',
    lines: [{ sku: '', item_name: '', qty: 1, unit_price_eur: 0, fine_au_g: null }], deleted_line_ids: [],
});

function OrdersTab({ c, reloadMeta }: { c: Ctx; reloadMeta: () => void }) {
    const { t, eur, meta } = c;
    const [f, setF] = useState<Record<string, string | boolean>>({ supplier_id: '', status: 'otvoreno', from: '', to: '', without_invoice: false, overdue: false, partial: false, delivery_plan_id: '', pg_run_id: '', q: '' });
    const [data, setData] = useState<{ items: OrderRow[]; totals: Record<string, number> } | null>(null);
    const [form, setForm] = useState<OrderForm | null>(null);
    const [pg, setPg] = useState<{ runId: string; expected_date: string; warehouse_code: string; delivery_plan_id: string } | null>(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        api<{ items: OrderRow[]; totals: Record<string, number> }>(`/import-orders/orders${qs(f)}`, c.token).then(setData).catch(c.onError);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [f, c.token]);
    useEffect(() => load(), [load]);

    const edit = async (id: number) => {
        try {
            const r = await api<{ order: OrderRow & { payment_terms: string | null; purpose: string; company: string | null }; lines: OrderLine[] }>(`/import-orders/orders/${id}`, c.token);
            const o = r.order;
            setForm({
                id, supplier_id: String(o.supplier_id), offer_no: o.offer_no ?? '', order_date: o.order_date?.slice(0, 10) ?? today(), expected_date: o.expected_date?.slice(0, 10) ?? '',
                warehouse_code: o.warehouse_code ?? '', delivery_plan_id: o.delivery_plan_id ? String(o.delivery_plan_id) : '', payment_terms: o.payment_terms ?? '',
                purpose: o.purpose ?? 'regular', company: o.company ?? '', note: o.note ?? '',
                lines: r.lines.map((l) => ({ id: l.id, sku: l.sku, item_name: l.item_name, qty: n(l.qty), qty_received: n(l.qty_received), unit_price_eur: n(l.unit_price_eur), fine_au_g: l.fine_au_g !== null ? n(l.fine_au_g) : null })),
                deleted_line_ids: [],
            });
        } catch (e) {
            c.onError(e);
        }
    };

    const save = async () => {
        if (!form) return;
        setBusy(true);
        const body: Record<string, unknown> = {
            supplier_id: Number(form.supplier_id), offer_no: form.offer_no || null, order_date: form.order_date, expected_date: form.expected_date || null,
            warehouse_code: form.warehouse_code || null, delivery_plan_id: form.delivery_plan_id ? Number(form.delivery_plan_id) : null,
            payment_terms: form.payment_terms || null, purpose: form.purpose, company: form.company || null, note: form.note || null,
            lines: form.lines.map((l) => ({ ...(l.id ? { id: l.id } : {}), sku: l.sku || null, item_name: l.item_name || null, qty: n(l.qty), unit_price_eur: n(l.unit_price_eur), fine_au_g: l.fine_au_g === null || String(l.fine_au_g) === '' ? null : n(l.fine_au_g) })),
            deleted_line_ids: form.deleted_line_ids,
        };
        try {
            if (form.id) await mutate(`/import-orders/orders/${form.id}`, c.token, 'PATCH', body);
            else await mutate('/import-orders/orders', c.token, 'POST', body);
            c.notify(t('Narudžba spremljena.', 'Order saved.'));
            setForm(null);
            load();
        } catch (e) {
            c.onError(e);
        } finally {
            setBusy(false);
        }
    };

    const setStatus = async (id: number, status: string) => {
        try {
            await mutate(`/import-orders/orders/${id}/status`, c.token, 'PATCH', { status });
            load();
        } catch (e) {
            c.onError(e);
        }
    };

    const remove = async (id: number) => {
        if (!window.confirm(t('Obrisati narudžbu? Briše se i redak u registru 40.4.', 'Delete order? The register 40.4 row is removed too.'))) return;
        try {
            await mutate(`/import-orders/orders/${id}`, c.token, 'DELETE');
            load();
        } catch (e) {
            c.onError(e);
        }
    };

    const fromPg = async () => {
        if (!pg?.runId) return;
        setBusy(true);
        try {
            const r = await mutate<{ order_ids: number[]; skipped: { razlog: string }[] }>(`/import-orders/from-pg-run/${pg.runId}`, c.token, 'POST', {
                expected_date: pg.expected_date || null, warehouse_code: pg.warehouse_code || null, delivery_plan_id: pg.delivery_plan_id ? Number(pg.delivery_plan_id) : null,
            });
            c.notify(t(`Napravljeno narudžbi ${r.order_ids.length}, preskočeno stavki ${r.skipped.length}.`, `Created ${r.order_ids.length} orders, skipped ${r.skipped.length} lines.`));
            setPg(null);
            reloadMeta();
            load();
        } catch (e) {
            c.onError(e);
        } finally {
            setBusy(false);
        }
    };

    const setLine = (i: number, patch: Partial<OrderLine>) => form && setForm({ ...form, lines: form.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
    const goods = meta.suppliers.filter((s) => s.kind === 'goods');

    return (
        <div>
            <div style={row}>
                <Field label={t('Dobavljač', 'Supplier')}>
                    <select value={String(f.supplier_id)} onChange={(e) => setF({ ...f, supplier_id: e.target.value })}>
                        <option value="">{t('Svi', 'All')}</option>
                        {meta.suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </Field>
                <Field label={t('Status', 'Status')}>
                    <select value={String(f.status)} onChange={(e) => setF({ ...f, status: e.target.value })}>
                        <option value="">{t('Svi', 'All')}</option>
                        <option value="otvoreno">{t('Otvorene', 'Open')}</option>
                        {Object.entries(meta.statuses).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                </Field>
                <Field label={t('Od', 'From')}><input type="date" value={String(f.from)} onChange={(e) => setF({ ...f, from: e.target.value })} /></Field>
                <Field label={t('Do', 'To')}><input type="date" value={String(f.to)} onChange={(e) => setF({ ...f, to: e.target.value })} /></Field>
                <Field label={t('Plan', 'Plan')}>
                    <select value={String(f.delivery_plan_id)} onChange={(e) => setF({ ...f, delivery_plan_id: e.target.value })}>
                        <option value="">{t('Svi', 'All')}</option>
                        {meta.plans.map((p) => <option key={p.id} value={p.id}>{p.plan_number}</option>)}
                    </select>
                </Field>
                <Field label={t('P&G pokretanje (id)', 'P&G run (id)')}><input value={String(f.pg_run_id)} onChange={(e) => setF({ ...f, pg_run_id: e.target.value })} style={{ width: 70 }} /></Field>
                <Field label={t('Traži', 'Search')}><input value={String(f.q)} onChange={(e) => setF({ ...f, q: e.target.value })} /></Field>
                {(['without_invoice', 'overdue', 'partial'] as const).map((k) => (
                    <label key={k} style={{ fontSize: '0.8rem' }}>
                        <input type="checkbox" checked={Boolean(f[k])} onChange={(e) => setF({ ...f, [k]: e.target.checked })} />{' '}
                        {k === 'without_invoice' ? t('bez računa', 'no invoice') : k === 'overdue' ? t('prošao očekivani dolazak', 'past expected date') : t('djelomično isporučene', 'partially delivered')}
                    </label>
                ))}
            </div>
            {meta.can_edit ? (
                <div style={row}>
                    <button type="button" className="compact" onClick={() => setForm(emptyOrder())}>{t('Nova narudžba', 'New order')}</button>
                    <button type="button" className="secondary compact" disabled={meta.pg_runs.length === 0} onClick={() => setPg({ runId: '', expected_date: '', warehouse_code: '', delivery_plan_id: '' })}>
                        {t(`Iz P&G (${meta.pg_runs.length})`, `From P&G (${meta.pg_runs.length})`)}
                    </button>
                </div>
            ) : null}

            {pg ? (
                <div style={box}>
                    <h3 style={{ margin: 0 }}>{t('Narudžbe iz poslanog P&G pokretanja', 'Orders from a sent P&G run')}</h3>
                    <p className="muted" style={{ margin: '0.2rem 0' }}>{t('Za svakog dobavljača nastaje jedna narudžba sa stavkama pokretanja. Isključene stavke i prijenosi ne ulaze.', 'One order per supplier is created from the run lines. Excluded lines and transfers are skipped.')}</p>
                    <div style={row}>
                        <Field label={t('Pokretanje', 'Run')}>
                            <select value={pg.runId} onChange={(e) => setPg({ ...pg, runId: e.target.value })}>
                                <option value="">-</option>
                                {meta.pg_runs.map((r) => (
                                    <option key={r.id} value={r.id}>{r.public_code} ({r.line_count} {t('stavki', 'lines')}, {eur(r.total_eur)}, {r.suppliers.join(', ')})</option>
                                ))}
                            </select>
                        </Field>
                        <Field label={t('Očekivani dolazak', 'Expected arrival')}><input type="date" value={pg.expected_date} onChange={(e) => setPg({ ...pg, expected_date: e.target.value })} /></Field>
                        <Field label={t('Skladište', 'Warehouse')}>
                            <select value={pg.warehouse_code} onChange={(e) => setPg({ ...pg, warehouse_code: e.target.value })}>
                                <option value="">-</option>
                                {meta.warehouses.map((w) => <option key={w.code} value={w.code}>{w.code} {w.name}</option>)}
                            </select>
                        </Field>
                        <Field label={t('Plan', 'Plan')}>
                            <select value={pg.delivery_plan_id} onChange={(e) => setPg({ ...pg, delivery_plan_id: e.target.value })}>
                                <option value="">-</option>
                                {meta.plans.map((p) => <option key={p.id} value={p.id}>{p.plan_number}</option>)}
                            </select>
                        </Field>
                        <button type="button" className="compact" disabled={busy || !pg.runId} onClick={fromPg}>{t('Napravi narudžbe', 'Create orders')}</button>
                        <button type="button" className="secondary compact" onClick={() => setPg(null)}>{t('Odustani', 'Cancel')}</button>
                    </div>
                </div>
            ) : null}

            {form ? (
                <div style={box}>
                    <h3 style={{ margin: 0 }}>{form.id ? t('Uredi narudžbu', 'Edit order') : t('Nova narudžba', 'New order')}</h3>
                    <div style={row}>
                        <Field label={t('Dobavljač', 'Supplier')}>
                            <select value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}>
                                <option value="">-</option>
                                {goods.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </Field>
                        <Field label={t('Broj ponude', 'Offer no.')}><input value={form.offer_no} onChange={(e) => setForm({ ...form, offer_no: e.target.value })} /></Field>
                        <Field label={t('Datum narudžbe', 'Order date')}><input type="date" value={form.order_date} onChange={(e) => setForm({ ...form, order_date: e.target.value })} /></Field>
                        <Field label={t('Očekivani dolazak', 'Expected arrival')}><input type="date" value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} /></Field>
                        <Field label={t('Odredišno skladište', 'Destination')}>
                            <select value={form.warehouse_code} onChange={(e) => setForm({ ...form, warehouse_code: e.target.value })}>
                                <option value="">-</option>
                                {meta.warehouses.map((w) => <option key={w.code} value={w.code}>{w.code} {w.name}</option>)}
                            </select>
                        </Field>
                        <Field label={t('Plan', 'Plan')}>
                            <select value={form.delivery_plan_id} onChange={(e) => setForm({ ...form, delivery_plan_id: e.target.value })}>
                                <option value="">-</option>
                                {meta.plans.map((p) => <option key={p.id} value={p.id}>{p.plan_number}</option>)}
                            </select>
                        </Field>
                        <Field label={t('Način plaćanja', 'Payment terms')}>
                            <select value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}>
                                <option value="">-</option>
                                {Object.entries(meta.payment_terms).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                        </Field>
                        <Field label={t('Namjena', 'Purpose')}>
                            <select value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })}>
                                {meta.purposes.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </Field>
                        <Field label={t('Tvrtka', 'Company')}><input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} style={{ width: 70 }} /></Field>
                    </div>
                    <table style={table}>
                        <thead>
                            <tr>
                                <th style={th}>{t('Šifra', 'SKU')}</th><th style={th}>{t('Naziv', 'Name')}</th><th style={thn}>{t('Količina', 'Qty')}</th>
                                <th style={thn}>{t('Jed. cijena EUR', 'Unit EUR')}</th><th style={thn}>{t('Ukupno', 'Total')}</th><th style={thn}>{t('Fine Au g', 'Fine Au g')}</th><th style={thn}>{t('Primljeno', 'Received')}</th><th style={th} />
                            </tr>
                        </thead>
                        <tbody>
                            {form.lines.map((l, i) => (
                                <tr key={l.id ?? `n${i}`}>
                                    <td style={td}><input value={l.sku ?? ''} onChange={(e) => setLine(i, { sku: e.target.value })} style={{ width: 90 }} /></td>
                                    <td style={td}><input value={l.item_name} onChange={(e) => setLine(i, { item_name: e.target.value })} style={{ width: 220 }} /></td>
                                    <td style={num}><input type="number" step="any" value={l.qty} onChange={(e) => setLine(i, { qty: Number(e.target.value) })} style={{ width: 80 }} /></td>
                                    <td style={num}><input type="number" step="any" value={l.unit_price_eur} onChange={(e) => setLine(i, { unit_price_eur: Number(e.target.value) })} style={{ width: 110 }} /></td>
                                    <td style={num}>{eur(n(l.qty) * n(l.unit_price_eur))}</td>
                                    <td style={num}><input type="number" step="any" value={l.fine_au_g ?? ''} onChange={(e) => setLine(i, { fine_au_g: e.target.value === '' ? null : Number(e.target.value) })} style={{ width: 90 }} /></td>
                                    <td style={num}>{l.qty_received !== undefined ? formatNumber(l.qty_received, c.locale) : ''}</td>
                                    <td style={td}>
                                        <button type="button" className="secondary compact" onClick={() => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i), deleted_line_ids: l.id ? [...form.deleted_line_ids, l.id] : form.deleted_line_ids })}>
                                            {t('Ukloni', 'Remove')}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div style={row}>
                        <button type="button" className="secondary compact" onClick={() => setForm({ ...form, lines: [...form.lines, { sku: '', item_name: '', qty: 1, unit_price_eur: 0, fine_au_g: null }] })}>{t('Dodaj stavku', 'Add line')}</button>
                        <span style={{ marginLeft: 'auto' }}>{t('Ukupno', 'Total')} {eur(form.lines.reduce((s, l) => s + n(l.qty) * n(l.unit_price_eur), 0))}</span>
                    </div>
                    <Field label={t('Napomena', 'Note')}><textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} /></Field>
                    <div style={row}>
                        <button type="button" className="compact" disabled={busy || !form.supplier_id || form.lines.length === 0} onClick={save}>{t('Spremi', 'Save')}</button>
                        <button type="button" className="secondary compact" onClick={() => setForm(null)}>{t('Odustani', 'Cancel')}</button>
                    </div>
                </div>
            ) : null}

            <div style={{ overflowX: 'auto', marginTop: '0.8rem' }}>
                <table style={table}>
                    <thead>
                        <tr>
                            <th style={th}>{t('Kod', 'Code')}</th><th style={th}>{t('Dobavljač', 'Supplier')}</th><th style={th}>{t('Naručeno', 'Ordered')}</th><th style={th}>{t('Očekivano', 'Expected')}</th>
                            <th style={th}>{t('Status', 'Status')}</th><th style={thn}>{t('Ukupno', 'Total')}</th><th style={thn}>{t('Otvoreno', 'Open')}</th><th style={thn}>{t('Na računima', 'Invoiced')}</th><th style={th}>{t('Napomena', 'Note')}</th>
                            {meta.can_edit ? <th style={th} /> : null}
                        </tr>
                    </thead>
                    <tbody>
                        {(data?.items ?? []).map((o) => (
                            <tr key={o.id}>
                                <td style={td}>{o.public_code}{o.pg_run_id ? <div className="muted" style={{ fontSize: '0.72rem' }}>P&G #{o.pg_run_id}</div> : null}</td>
                                <td style={td}>{o.supplier_name}</td>
                                <td style={td}>{c.date(o.order_date)}</td>
                                <td style={{ ...td, fontWeight: n(o.overdue) ? 700 : 400 }}>{c.date(o.expected_date)}</td>
                                <td style={td}>
                                    {meta.can_edit ? (
                                        <select value={o.status} onChange={(e) => void setStatus(o.id, e.target.value)}>
                                            {Object.entries(meta.statuses).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                        </select>
                                    ) : meta.statuses[o.status] ?? o.status}
                                    {n(o.partial) ? <div className="muted" style={{ fontSize: '0.72rem' }}>{t('djelomično', 'partial')}</div> : null}
                                </td>
                                <td style={num}>{eur(o.total_eur)}</td>
                                <td style={num}>{eur(o.open_eur)}</td>
                                <td style={num}>{eur(o.invoiced_eur)}</td>
                                <td style={td}>{o.note}</td>
                                {meta.can_edit ? (
                                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                        <button type="button" className="secondary compact" onClick={() => void edit(o.id)}>{t('Uredi', 'Edit')}</button>{' '}
                                        <button type="button" className="secondary compact" onClick={() => void remove(o.id)}>{t('Obriši', 'Delete')}</button>
                                    </td>
                                ) : null}
                            </tr>
                        ))}
                        {data ? (
                            <tr>
                                <td style={{ ...td, fontWeight: 700 }} colSpan={5}>{t(`Ukupno ${data.totals.count} narudžbi`, `Total ${data.totals.count} orders`)}</td>
                                <td style={{ ...num, fontWeight: 700 }}>{eur(data.totals.total_eur)}</td>
                                <td style={{ ...num, fontWeight: 700 }}>{eur(data.totals.open_eur)}</td>
                                <td style={{ ...num, fontWeight: 700 }}>{eur(data.totals.invoiced_eur)}</td>
                                <td style={td} colSpan={meta.can_edit ? 2 : 1} />
                            </tr>
                        ) : null}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

type InvoiceForm = {
    id?: number; supplier_id: string; kind: string; invoice_no: string; invoice_date: string; due_date: string; amount_eur: string; fine_au_g: string; fine_ag_g: string;
    transported_value_eur: string; transported_fine_au_g: string; transported_fine_ag_g: string; reference_text: string; company: string; goods_arrived: boolean; note: string;
    allocations: { order_id: string; allocated_eur: string }[];
};
const emptyInvoice = (): InvoiceForm => ({
    supplier_id: '', kind: 'roba', invoice_no: '', invoice_date: today(), due_date: '', amount_eur: '', fine_au_g: '', fine_ag_g: '', transported_value_eur: '',
    transported_fine_au_g: '', transported_fine_ag_g: '', reference_text: '', company: '', goods_arrived: false, note: '', allocations: [],
});
const numOrNull = (s: string) => (s.trim() === '' ? null : Number(s.replace(',', '.')));

function InvoicesTab({ c }: { c: Ctx }) {
    const { t, eur, meta } = c;
    const [f, setF] = useState<Record<string, string | boolean>>({ supplier_id: '', kind: '', from: '', to: '', payment_state: '', ageing: '', without_allocation: false, unallocated_rest: false, source: '', q: '' });
    const [data, setData] = useState<{ items: InvoiceRow[]; totals: Record<string, number> } | null>(null);
    const [form, setForm] = useState<InvoiceForm | null>(null);
    const [orders, setOrders] = useState<OrderRow[]>([]);
    const [pay, setPay] = useState<{ id: number; open: number; paid_on: string; amount: string; note: string; list: { id: number; paid_on: string | null; amount_eur: number; note: string | null }[] } | null>(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        api<{ items: InvoiceRow[]; totals: Record<string, number> }>(`/import-orders/invoices${qs(f)}`, c.token).then(setData).catch(c.onError);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [f, c.token]);
    useEffect(() => load(), [load]);

    const loadOrders = (supplierId: string) => {
        if (!supplierId) return setOrders([]);
        api<{ items: OrderRow[] }>(`/import-orders/orders?supplier_id=${supplierId}`, c.token).then((r) => setOrders(r.items)).catch(c.onError);
    };

    const edit = async (id: number) => {
        try {
            const r = await api<{ invoice: InvoiceRow & Record<string, unknown>; allocations: { order_id: number; allocated_eur: number }[] }>(`/import-orders/invoices/${id}`, c.token);
            const i = r.invoice;
            const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));
            setForm({
                id, supplier_id: String(i.supplier_id), kind: i.kind, invoice_no: s(i.invoice_no), invoice_date: s(i.invoice_date).slice(0, 10), due_date: s(i.due_date).slice(0, 10),
                amount_eur: s(i.amount_eur), fine_au_g: s(i.fine_au_g), fine_ag_g: s(i.fine_ag_g), transported_value_eur: s(i.transported_value_eur),
                transported_fine_au_g: s(i.transported_fine_au_g), transported_fine_ag_g: s(i.transported_fine_ag_g), reference_text: s(i.reference_text), company: s(i.company),
                goods_arrived: Boolean(Number(i.goods_arrived ?? 0)), note: s(i.note),
                allocations: r.allocations.map((a) => ({ order_id: String(a.order_id), allocated_eur: String(a.allocated_eur) })),
            });
            loadOrders(String(i.supplier_id));
        } catch (e) {
            c.onError(e);
        }
    };

    const save = async () => {
        if (!form) return;
        setBusy(true);
        const body: Record<string, unknown> = {
            supplier_id: Number(form.supplier_id), kind: form.kind, invoice_no: form.invoice_no || null, invoice_date: form.invoice_date || null, due_date: form.due_date || null,
            amount_eur: numOrNull(form.amount_eur) ?? 0, fine_au_g: numOrNull(form.fine_au_g), fine_ag_g: numOrNull(form.fine_ag_g),
            transported_value_eur: numOrNull(form.transported_value_eur), transported_fine_au_g: numOrNull(form.transported_fine_au_g), transported_fine_ag_g: numOrNull(form.transported_fine_ag_g),
            reference_text: form.reference_text || null, company: form.company || null, goods_arrived: form.goods_arrived, note: form.note || null,
            allocations: form.allocations.filter((a) => a.order_id).map((a) => ({ order_id: Number(a.order_id), allocated_eur: numOrNull(a.allocated_eur) ?? 0 })),
        };
        try {
            if (form.id) await mutate(`/import-orders/invoices/${form.id}`, c.token, 'PATCH', body);
            else await mutate('/import-orders/invoices', c.token, 'POST', body);
            c.notify(t('Račun spremljen.', 'Invoice saved.'));
            setForm(null);
            load();
        } catch (e) {
            c.onError(e);
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id: number) => {
        if (!window.confirm(t('Obrisati račun? Briše se i neplaćena obveza u planu isplate.', 'Delete invoice? The unpaid payment schedule entry is removed too.'))) return;
        try {
            await mutate(`/import-orders/invoices/${id}`, c.token, 'DELETE');
            load();
        } catch (e) {
            c.onError(e);
        }
    };

    const openPay = async (i: InvoiceRow) => {
        try {
            const r = await api<{ payments: { id: number; paid_on: string | null; amount_eur: number; note: string | null }[] }>(`/import-orders/invoices/${i.id}`, c.token);
            setPay({ id: i.id, open: n(i.open_eur), paid_on: today(), amount: String(n(i.open_eur)), note: '', list: r.payments });
        } catch (e) {
            c.onError(e);
        }
    };
    const addPay = async () => {
        if (!pay) return;
        try {
            await mutate(`/import-orders/invoices/${pay.id}/payments`, c.token, 'POST', { paid_on: pay.paid_on || null, amount_eur: numOrNull(pay.amount) ?? 0, note: pay.note || null });
            c.notify(t('Plaćanje upisano.', 'Payment recorded.'));
            setPay(null);
            load();
        } catch (e) {
            c.onError(e);
        }
    };
    const delPay = async (pid: number) => {
        if (!pay) return;
        try {
            await mutate(`/import-orders/invoices/${pay.id}/payments/${pid}`, c.token, 'DELETE');
            setPay(null);
            load();
        } catch (e) {
            c.onError(e);
        }
    };

    const allocated = form ? form.allocations.reduce((s, a) => s + (numOrNull(a.allocated_eur) ?? 0), 0) : 0;
    const set = (patch: Partial<InvoiceForm>) => form && setForm({ ...form, ...patch });

    return (
        <div>
            <div style={row}>
                <Field label={t('Dobavljač', 'Supplier')}>
                    <select value={String(f.supplier_id)} onChange={(e) => setF({ ...f, supplier_id: e.target.value })}>
                        <option value="">{t('Svi', 'All')}</option>
                        {meta.suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </Field>
                <Field label={t('Vrsta', 'Kind')}>
                    <select value={String(f.kind)} onChange={(e) => setF({ ...f, kind: e.target.value })}>
                        <option value="">{t('Sve', 'All')}</option>
                        {Object.entries(meta.invoice_kinds).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                </Field>
                <Field label={t('Od', 'From')}><input type="date" value={String(f.from)} onChange={(e) => setF({ ...f, from: e.target.value })} /></Field>
                <Field label={t('Do', 'To')}><input type="date" value={String(f.to)} onChange={(e) => setF({ ...f, to: e.target.value })} /></Field>
                <Field label={t('Stanje plaćanja', 'Payment state')}>
                    <select value={String(f.payment_state)} onChange={(e) => setF({ ...f, payment_state: e.target.value })}>
                        <option value="">{t('Sva', 'All')}</option>
                        {Object.entries(meta.payment_states).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                </Field>
                <Field label={t('Starost dospijeća', 'Ageing')}>
                    <select value={String(f.ageing)} onChange={(e) => setF({ ...f, ageing: e.target.value })}>
                        <option value="">{t('Sve', 'All')}</option>
                        {Object.entries(meta.ageing).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                </Field>
                <Field label={t('Izvor', 'Source')}>
                    <select value={String(f.source)} onChange={(e) => setF({ ...f, source: e.target.value })}>
                        <option value="">{t('Svi', 'All')}</option>
                        <option value="manual">{t('Unos', 'Manual')}</option>
                        <option value="excel">Excel</option>
                    </select>
                </Field>
                <Field label={t('Traži', 'Search')}><input value={String(f.q)} onChange={(e) => setF({ ...f, q: e.target.value })} /></Field>
                <label style={{ fontSize: '0.8rem' }}><input type="checkbox" checked={Boolean(f.without_allocation)} onChange={(e) => setF({ ...f, without_allocation: e.target.checked })} /> {t('bez raspoređene narudžbe', 'no order allocated')}</label>
                <label style={{ fontSize: '0.8rem' }}><input type="checkbox" checked={Boolean(f.unallocated_rest)} onChange={(e) => setF({ ...f, unallocated_rest: e.target.checked })} /> {t('s neraspoređenim ostatkom', 'with unallocated rest')}</label>
            </div>
            {meta.can_edit ? (
                <div style={row}>
                    <button type="button" className="compact" onClick={() => { setForm(emptyInvoice()); setOrders([]); }}>{t('Novi račun', 'New invoice')}</button>
                </div>
            ) : null}

            {form ? (
                <div style={box}>
                    <h3 style={{ margin: 0 }}>{form.id ? t('Uredi račun', 'Edit invoice') : t('Novi račun', 'New invoice')}</h3>
                    <div style={row}>
                        <Field label={t('Dobavljač', 'Supplier')}>
                            <select value={form.supplier_id} onChange={(e) => { set({ supplier_id: e.target.value, allocations: [] }); loadOrders(e.target.value); }}>
                                <option value="">-</option>
                                {meta.suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </Field>
                        <Field label={t('Vrsta', 'Kind')}>
                            <select value={form.kind} onChange={(e) => set({ kind: e.target.value })}>
                                {Object.entries(meta.invoice_kinds).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                        </Field>
                        <Field label={t('Broj računa', 'Invoice no.')}><input value={form.invoice_no} onChange={(e) => set({ invoice_no: e.target.value })} /></Field>
                        <Field label={t('Datum računa', 'Invoice date')}><input type="date" value={form.invoice_date} onChange={(e) => set({ invoice_date: e.target.value })} /></Field>
                        <Field label={t('Dospijeće (prazno = po dobavljaču)', 'Due (empty = supplier default)')}><input type="date" value={form.due_date} onChange={(e) => set({ due_date: e.target.value })} /></Field>
                        <Field label={t('Iznos EUR', 'Amount EUR')}><input value={form.amount_eur} onChange={(e) => set({ amount_eur: e.target.value })} style={{ width: 120 }} /></Field>
                        <Field label="FAu g"><input value={form.fine_au_g} onChange={(e) => set({ fine_au_g: e.target.value })} style={{ width: 90 }} /></Field>
                        <Field label="FAg g"><input value={form.fine_ag_g} onChange={(e) => set({ fine_ag_g: e.target.value })} style={{ width: 90 }} /></Field>
                        <Field label={t('Tvrtka', 'Company')}><input value={form.company} onChange={(e) => set({ company: e.target.value })} style={{ width: 70 }} /></Field>
                    </div>
                    {form.kind === 'prijevoz' ? (
                        <div style={row}>
                            <Field label={t('Prevezena vrijednost EUR', 'Transported value EUR')}><input value={form.transported_value_eur} onChange={(e) => set({ transported_value_eur: e.target.value })} /></Field>
                            <Field label={t('Prevezeni fine Au g', 'Transported fine Au g')}><input value={form.transported_fine_au_g} onChange={(e) => set({ transported_fine_au_g: e.target.value })} /></Field>
                            <Field label={t('Prevezeni fine Ag g', 'Transported fine Ag g')}><input value={form.transported_fine_ag_g} onChange={(e) => set({ transported_fine_ag_g: e.target.value })} /></Field>
                        </div>
                    ) : null}
                    <h4 style={{ margin: '0.6rem 0 0.2rem' }}>{t('Raspodjela po narudžbama', 'Allocation to orders')}</h4>
                    {form.allocations.map((a, i) => (
                        <div key={i} style={row}>
                            <select value={a.order_id} onChange={(e) => set({ allocations: form.allocations.map((x, j) => (j === i ? { ...x, order_id: e.target.value } : x)) })}>
                                <option value="">-</option>
                                {orders.map((o) => <option key={o.id} value={o.id}>{o.public_code} ({eur(o.total_eur)})</option>)}
                            </select>
                            <input value={a.allocated_eur} onChange={(e) => set({ allocations: form.allocations.map((x, j) => (j === i ? { ...x, allocated_eur: e.target.value } : x)) })} style={{ width: 120 }} />
                            <button type="button" className="secondary compact" onClick={() => set({ allocations: form.allocations.filter((_, j) => j !== i) })}>{t('Ukloni', 'Remove')}</button>
                        </div>
                    ))}
                    <div style={row}>
                        <button type="button" className="secondary compact" disabled={!form.supplier_id} onClick={() => set({ allocations: [...form.allocations, { order_id: '', allocated_eur: '' }] })}>{t('Dodaj narudžbu', 'Add order')}</button>
                        <span className="muted">{t('Neraspoređeno', 'Unallocated')} {eur((numOrNull(form.amount_eur) ?? 0) - allocated)}</span>
                        {form.allocations.length === 0 ? (
                            <label style={{ fontSize: '0.8rem' }}><input type="checkbox" checked={form.goods_arrived} onChange={(e) => set({ goods_arrived: e.target.checked })} /> {t('roba stigla (račun bez narudžbe)', 'goods arrived (no order)')}</label>
                        ) : null}
                    </div>
                    <Field label={t('Referenca', 'Reference')}><input value={form.reference_text} onChange={(e) => set({ reference_text: e.target.value })} style={{ width: '100%' }} /></Field>
                    <Field label={t('Napomena', 'Note')}><textarea value={form.note} onChange={(e) => set({ note: e.target.value })} rows={2} /></Field>
                    <div style={row}>
                        <button type="button" className="compact" disabled={busy || !form.supplier_id || form.amount_eur === ''} onClick={save}>{t('Spremi', 'Save')}</button>
                        <button type="button" className="secondary compact" onClick={() => setForm(null)}>{t('Odustani', 'Cancel')}</button>
                    </div>
                </div>
            ) : null}

            {pay ? (
                <div style={box}>
                    <h3 style={{ margin: 0 }}>{t('Plaćanja', 'Payments')}</h3>
                    {pay.list.map((p) => (
                        <div key={p.id} style={row}>
                            <span>{p.paid_on ? c.date(p.paid_on) : t('datum nepoznat', 'date unknown')}</span>
                            <span>{eur(p.amount_eur)}</span>
                            <span className="muted">{p.note}</span>
                            {meta.can_edit ? <button type="button" className="secondary compact" onClick={() => void delPay(p.id)}>{t('Obriši', 'Delete')}</button> : null}
                        </div>
                    ))}
                    {meta.can_edit && pay.open > 0.005 ? (
                        <div style={row}>
                            <Field label={t('Datum plaćanja', 'Paid on')}><input type="date" value={pay.paid_on} onChange={(e) => setPay({ ...pay, paid_on: e.target.value })} /></Field>
                            <Field label={t('Iznos EUR', 'Amount EUR')}><input value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} style={{ width: 120 }} /></Field>
                            <Field label={t('Napomena', 'Note')}><input value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} /></Field>
                            <button type="button" className="compact" onClick={addPay}>{t('Upiši plaćanje', 'Record payment')}</button>
                        </div>
                    ) : null}
                    <button type="button" className="secondary compact" onClick={() => setPay(null)}>{t('Zatvori', 'Close')}</button>
                </div>
            ) : null}

            <div style={{ overflowX: 'auto', marginTop: '0.8rem' }}>
                <table style={table}>
                    <thead>
                        <tr>
                            <th style={th}>{t('Dobavljač', 'Supplier')}</th><th style={th}>{t('Vrsta', 'Kind')}</th><th style={th}>{t('Broj', 'No.')}</th><th style={th}>{t('Datum', 'Date')}</th>
                            <th style={th}>{t('Dospijeće', 'Due')}</th><th style={thn}>{t('Iznos', 'Amount')}</th><th style={thn}>{t('Plaćeno', 'Paid')}</th><th style={thn}>{t('Otvoreno', 'Open')}</th>
                            <th style={th}>{t('Stanje', 'State')}</th><th style={thn}>{t('Neraspoređeno', 'Unallocated')}</th><th style={th}>{t('Referenca', 'Reference')}</th><th style={th} />
                        </tr>
                    </thead>
                    <tbody>
                        {(data?.items ?? []).map((i) => (
                            <tr key={i.id}>
                                <td style={td}>{i.supplier_name}</td>
                                <td style={td}>{meta.invoice_kinds[i.kind] ?? i.kind}</td>
                                <td style={td}>{i.invoice_no ?? '-'}{i.source === 'excel' ? <div className="muted" style={{ fontSize: '0.72rem' }}>Excel{i.flags ? `, ${i.flags.replace(/,/g, ', ')}` : ''}</div> : null}</td>
                                <td style={td}>{c.date(i.invoice_date)}</td>
                                <td style={td}>{c.date(i.due_date)}{i.ageing && i.ageing !== 'nije_dospjelo' ? <div style={{ fontSize: '0.72rem', fontWeight: 700 }}>{meta.ageing[i.ageing]}</div> : null}</td>
                                <td style={num}>{eur(i.amount_eur)}</td>
                                <td style={num}>{eur(i.paid_eur)}</td>
                                <td style={num}>{eur(i.open_eur)}</td>
                                <td style={td}>{meta.payment_states[i.payment_state] ?? i.payment_state}</td>
                                <td style={num}>{n(i.unallocated_eur) > 0.005 ? eur(i.unallocated_eur) : ''}</td>
                                <td style={{ ...td, maxWidth: 240 }}>{i.reference_text}</td>
                                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                    <button type="button" className="secondary compact" onClick={() => void openPay(i)}>{t('Plaćanja', 'Payments')}</button>{' '}
                                    {meta.can_edit ? (
                                        <>
                                            <button type="button" className="secondary compact" onClick={() => void edit(i.id)}>{t('Uredi', 'Edit')}</button>{' '}
                                            <button type="button" className="secondary compact" onClick={() => void remove(i.id)}>{t('Obriši', 'Delete')}</button>
                                        </>
                                    ) : null}
                                </td>
                            </tr>
                        ))}
                        {data ? (
                            <tr>
                                <td style={{ ...td, fontWeight: 700 }} colSpan={5}>{t(`Ukupno ${data.totals.count} računa`, `Total ${data.totals.count} invoices`)}</td>
                                <td style={{ ...num, fontWeight: 700 }}>{eur(data.totals.amount_eur)}</td>
                                <td style={{ ...num, fontWeight: 700 }}>{eur(data.totals.paid_eur)}</td>
                                <td style={{ ...num, fontWeight: 700 }}>{eur(data.totals.unpaid_eur)}</td>
                                <td style={td}>{t('plaćeno, nije stiglo', 'paid, not arrived')} {eur(data.totals.paid_not_arrived_eur)}</td>
                                <td style={{ ...num, fontWeight: 700 }}>{eur(data.totals.unallocated_eur)}</td>
                                <td style={td} colSpan={2} />
                            </tr>
                        ) : null}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

type DeliveryRow = { id: number; public_code: string; supplier_name: string; arrived_at: string; arrival_method: string | null; tracking_no: string | null; warehouse_code: string | null; qty: number; confirmed_by_name: string | null; inbound_shipment_id: number | null };
type InboundRow = { id: number; arrived_at: string; carrier: string; tracking_no: string | null; sender: string | null; origin_city: string | null; gross_g: number | null; status: string };

function ArrivalsTab({ c }: { c: Ctx }) {
    const { t, eur, meta } = c;
    const [data, setData] = useState<{ items: DeliveryRow[]; waiting: OrderRow[]; inbound: InboundRow[] } | null>(null);
    const [conf, setConf] = useState<{ order: OrderRow; lines: OrderLine[]; qty: Record<number, string>; arrived_at: string; arrival_method: string; tracking_no: string; inbound_shipment_id: string; note: string } | null>(null);
    const [busy, setBusy] = useState(false);
    const load = useCallback(() => {
        api<{ items: DeliveryRow[]; waiting: OrderRow[]; inbound: InboundRow[] }>('/import-orders/deliveries', c.token).then(setData).catch(c.onError);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [c.token]);
    useEffect(() => load(), [load]);

    const start = async (o: OrderRow) => {
        try {
            const r = await api<{ lines: OrderLine[] }>(`/import-orders/orders/${o.id}`, c.token);
            const qty: Record<number, string> = {};
            r.lines.forEach((l) => {
                if (l.id) qty[l.id] = String(Math.max(0, n(l.qty) - n(l.qty_received)));
            });
            setConf({ order: o, lines: r.lines, qty, arrived_at: today(), arrival_method: '', tracking_no: '', inbound_shipment_id: '', note: '' });
        } catch (e) {
            c.onError(e);
        }
    };
    const confirm = async () => {
        if (!conf) return;
        setBusy(true);
        try {
            const r = await mutate<{ status: string }>(`/import-orders/orders/${conf.order.id}/deliveries`, c.token, 'POST', {
                arrived_at: conf.arrived_at, arrival_method: conf.arrival_method || null, tracking_no: conf.tracking_no || null,
                inbound_shipment_id: conf.inbound_shipment_id ? Number(conf.inbound_shipment_id) : null, note: conf.note || null,
                lines: Object.entries(conf.qty).map(([id, q]) => ({ order_line_id: Number(id), qty: numOrNull(q) ?? 0 })),
            });
            c.notify(t(`Dolazak potvrđen, status narudžbe ${meta.statuses[r.status] ?? r.status}.`, `Arrival confirmed, order status ${meta.statuses[r.status] ?? r.status}.`));
            setConf(null);
            load();
        } catch (e) {
            c.onError(e);
        } finally {
            setBusy(false);
        }
    };

    if (!data) return <p className="muted">{t('Učitavam...', 'Loading...')}</p>;
    return (
        <div>
            <h3 style={{ margin: '0 0 0.4rem' }}>{t('Čeka dolazak', 'Awaiting arrival')}</h3>
            <table style={table}>
                <thead>
                    <tr><th style={th}>{t('Kod', 'Code')}</th><th style={th}>{t('Dobavljač', 'Supplier')}</th><th style={th}>{t('Očekivano', 'Expected')}</th><th style={th}>{t('Status', 'Status')}</th><th style={thn}>{t('Otvoreno', 'Open')}</th><th style={th} /></tr>
                </thead>
                <tbody>
                    {data.waiting.map((o) => (
                        <tr key={o.id}>
                            <td style={td}>{o.public_code}</td><td style={td}>{o.supplier_name}</td>
                            <td style={{ ...td, fontWeight: n(o.overdue) ? 700 : 400 }}>{c.date(o.expected_date)}</td>
                            <td style={td}>{meta.statuses[o.status] ?? o.status}</td><td style={num}>{eur(o.open_eur)}</td>
                            <td style={td}>{meta.can_edit ? <button type="button" className="compact" onClick={() => void start(o)}>{t('Potvrdi dolazak', 'Confirm arrival')}</button> : null}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {data.waiting.length === 0 ? <p className="muted">{t('Nema otvorenih narudžbi.', 'No open orders.')}</p> : null}

            {conf ? (
                <div style={box}>
                    <h3 style={{ margin: 0 }}>{t('Potvrda dolaska', 'Arrival confirmation')} {conf.order.public_code}</h3>
                    <div style={row}>
                        <Field label={t('Datum dolaska', 'Arrived on')}><input type="date" value={conf.arrived_at} onChange={(e) => setConf({ ...conf, arrived_at: e.target.value })} /></Field>
                        <Field label={t('Način dolaska', 'Method')}>
                            <select value={conf.arrival_method} onChange={(e) => setConf({ ...conf, arrival_method: e.target.value })}>
                                <option value="">-</option>
                                {Object.entries(meta.arrival_methods).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                        </Field>
                        <Field label={t('Broj praćenja', 'Tracking no.')}><input value={conf.tracking_no} onChange={(e) => setConf({ ...conf, tracking_no: e.target.value })} /></Field>
                        {meta.inbound_available ? (
                            <Field label={t('Dolazna pošiljka (modul 45)', 'Inbound shipment (module 45)')}>
                                <select value={conf.inbound_shipment_id} onChange={(e) => setConf({ ...conf, inbound_shipment_id: e.target.value })}>
                                    <option value="">-</option>
                                    {data.inbound.map((s) => <option key={s.id} value={s.id}>{c.date(s.arrived_at)} {s.carrier} {s.tracking_no ?? ''} {s.sender ?? s.origin_city ?? ''}</option>)}
                                </select>
                            </Field>
                        ) : null}
                    </div>
                    <table style={table}>
                        <thead><tr><th style={th}>{t('Artikl', 'Item')}</th><th style={thn}>{t('Naručeno', 'Ordered')}</th><th style={thn}>{t('Već primljeno', 'Received')}</th><th style={thn}>{t('Stiglo sada', 'Arrived now')}</th></tr></thead>
                        <tbody>
                            {conf.lines.map((l) => (
                                <tr key={l.id}>
                                    <td style={td}>{l.sku} {l.item_name}</td><td style={num}>{formatNumber(n(l.qty), c.locale)}</td><td style={num}>{formatNumber(n(l.qty_received), c.locale)}</td>
                                    <td style={num}><input value={l.id ? conf.qty[l.id] ?? '' : ''} onChange={(e) => l.id && setConf({ ...conf, qty: { ...conf.qty, [l.id]: e.target.value } })} style={{ width: 80 }} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <Field label={t('Napomena', 'Note')}><input value={conf.note} onChange={(e) => setConf({ ...conf, note: e.target.value })} style={{ width: '100%' }} /></Field>
                    <div style={row}>
                        <button type="button" className="compact" disabled={busy} onClick={confirm}>{t('Potvrdi', 'Confirm')}</button>
                        <button type="button" className="secondary compact" onClick={() => setConf(null)}>{t('Odustani', 'Cancel')}</button>
                    </div>
                </div>
            ) : null}

            <h3 style={{ margin: '1rem 0 0.4rem' }}>{t('Potvrđeni dolasci', 'Confirmed arrivals')}</h3>
            <table style={table}>
                <thead>
                    <tr><th style={th}>{t('Datum', 'Date')}</th><th style={th}>{t('Narudžba', 'Order')}</th><th style={th}>{t('Dobavljač', 'Supplier')}</th><th style={th}>{t('Način', 'Method')}</th><th style={th}>{t('Praćenje', 'Tracking')}</th><th style={thn}>{t('Kom', 'Pcs')}</th><th style={th}>{t('Potvrdio', 'Confirmed by')}</th></tr>
                </thead>
                <tbody>
                    {data.items.map((d) => (
                        <tr key={d.id}>
                            <td style={td}>{c.date(d.arrived_at)}</td><td style={td}>{d.public_code}</td><td style={td}>{d.supplier_name}</td>
                            <td style={td}>{d.arrival_method ? meta.arrival_methods[d.arrival_method] ?? d.arrival_method : ''}</td><td style={td}>{d.tracking_no}</td>
                            <td style={num}>{formatNumber(n(d.qty), c.locale)}</td><td style={td}>{d.confirmed_by_name}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

type TransportRow = InvoiceRow & { transported_value_eur: number | null; fine_au_total: number | null; fine_ag_total: number | null; cost_per_g_au: number | null; cost_per_g_ag: number | null; delivery_count: number };

function TransportTab({ c }: { c: Ctx }) {
    const { t, eur, meta } = c;
    const [supplier, setSupplier] = useState('');
    const [data, setData] = useState<{ items: TransportRow[]; totals: Record<string, number | null> } | null>(null);
    useEffect(() => {
        api<{ items: TransportRow[]; totals: Record<string, number | null> }>(`/import-orders/transport${qs({ supplier_id: supplier })}`, c.token).then(setData).catch(c.onError);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supplier, c.token]);
    const perG = (v: number | null) => (v === null ? '-' : `${formatNumber(Math.round(n(v) * 10000) / 10000, c.locale)} €/g`);
    return (
        <div>
            <div style={row}>
                <Field label={t('Prijevoznik', 'Carrier')}>
                    <select value={supplier} onChange={(e) => setSupplier(e.target.value)}>
                        <option value="">{t('Svi', 'All')}</option>
                        {meta.suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </Field>
                <span className="muted">{t('Računi prijevoza unose se na kartici Računi, vrsta Prijevoz.', 'Transport invoices are entered on the Invoices tab, kind Transport.')}</span>
            </div>
            {data ? (
                <table style={table}>
                    <thead>
                        <tr>
                            <th style={th}>{t('Prijevoznik', 'Carrier')}</th><th style={th}>{t('Broj', 'No.')}</th><th style={th}>{t('Datum', 'Date')}</th><th style={thn}>{t('Iznos', 'Amount')}</th>
                            <th style={thn}>{t('Prevezena vrijednost', 'Transported value')}</th><th style={thn}>{t('Fine Au g', 'Fine Au g')}</th><th style={thn}>{t('Fine Ag g', 'Fine Ag g')}</th>
                            <th style={thn}>{t('Trošak po g Au', 'Cost per g Au')}</th><th style={thn}>{t('Trošak po g Ag', 'Cost per g Ag')}</th><th style={th}>{t('Plaćeno', 'Paid')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.items.map((r) => (
                            <tr key={r.id}>
                                <td style={td}>{r.supplier_name}</td><td style={td}>{r.invoice_no}</td><td style={td}>{c.date(r.invoice_date)}</td><td style={num}>{eur(r.amount_eur)}</td>
                                <td style={num}>{r.transported_value_eur !== null ? eur(r.transported_value_eur) : ''}</td>
                                <td style={num}>{r.fine_au_total !== null ? formatNumber(n(r.fine_au_total), c.locale) : ''}</td>
                                <td style={num}>{r.fine_ag_total !== null ? formatNumber(n(r.fine_ag_total), c.locale) : ''}</td>
                                <td style={num}>{perG(r.cost_per_g_au)}</td><td style={num}>{perG(r.cost_per_g_ag)}</td>
                                <td style={td}>{meta.payment_states[r.payment_state] ?? r.payment_state}</td>
                            </tr>
                        ))}
                        <tr>
                            <td style={{ ...td, fontWeight: 700 }} colSpan={3}>{t('Ukupno', 'Total')}</td>
                            <td style={{ ...num, fontWeight: 700 }}>{eur(data.totals.amount_eur)}</td>
                            <td style={{ ...num, fontWeight: 700 }}>{eur(data.totals.transported_value_eur)}</td>
                            <td style={{ ...num, fontWeight: 700 }}>{formatNumber(n(data.totals.fine_au_g), c.locale)}</td>
                            <td style={{ ...num, fontWeight: 700 }}>{formatNumber(n(data.totals.fine_ag_g), c.locale)}</td>
                            <td style={{ ...num, fontWeight: 700 }}>{perG(data.totals.cost_per_g_au)}</td>
                            <td style={{ ...num, fontWeight: 700 }}>{perG(data.totals.cost_per_g_ag)}</td>
                            <td style={td} />
                        </tr>
                    </tbody>
                </table>
            ) : null}
        </div>
    );
}

type ReviewRow = { id: number; sheet: string; row_no: number; reason: string; raw: unknown[]; resolved_at: string | null; resolution_note: string | null };

function ReviewTab({ c }: { c: Ctx }) {
    const { t, meta } = c;
    const [resolved, setResolved] = useState(false);
    const [data, setData] = useState<{ items: ReviewRow[]; open_by_sheet: Record<string, number> } | null>(null);
    const load = useCallback(() => {
        api<{ items: ReviewRow[]; open_by_sheet: Record<string, number> }>(`/import-orders/review${qs({ resolved })}`, c.token).then(setData).catch(c.onError);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolved, c.token]);
    useEffect(() => load(), [load]);
    const resolve = async (id: number, reopen: boolean) => {
        const note = reopen ? null : window.prompt(t('Kako je riješeno?', 'How was it resolved?'), '');
        if (!reopen && note === null) return;
        try {
            await mutate(`/import-orders/review/${id}/resolve`, c.token, 'PATCH', { resolution_note: note, reopen });
            load();
        } catch (e) {
            c.onError(e);
        }
    };
    if (!data) return <p className="muted">{t('Učitavam...', 'Loading...')}</p>;
    return (
        <div>
            <p className="muted">
                {t('Redci iz Excela koje uvoz nije mogao sigurno protumačiti. Račun se po potrebi upiše ručno na kartici Računi, a redak označi riješenim.', 'Excel rows the import could not parse safely. Enter the invoice manually if needed and mark the row resolved.')}{' '}
                {Object.entries(data.open_by_sheet).map(([s, k]) => `${s} ${k}`).join(', ')}
            </p>
            <label style={{ fontSize: '0.8rem' }}><input type="checkbox" checked={resolved} onChange={(e) => setResolved(e.target.checked)} /> {t('prikaži riješene', 'show resolved')}</label>
            <table style={table}>
                <thead><tr><th style={th}>{t('List', 'Sheet')}</th><th style={thn}>{t('Redak', 'Row')}</th><th style={th}>{t('Razlog', 'Reason')}</th><th style={th}>{t('Vrijednosti', 'Values')}</th><th style={th} /></tr></thead>
                <tbody>
                    {data.items.map((r) => (
                        <tr key={r.id}>
                            <td style={td}>{r.sheet}</td><td style={num}>{r.row_no}</td><td style={td}>{r.reason}</td>
                            <td style={{ ...td, fontSize: '0.78rem' }}>{(r.raw ?? []).map((v) => (v === null ? '' : String(v))).filter((v) => v !== '').join(' | ')}</td>
                            <td style={td}>
                                {meta.can_edit ? (
                                    <button type="button" className="secondary compact" onClick={() => void resolve(r.id, Boolean(r.resolved_at))}>{r.resolved_at ? t('Vrati', 'Reopen') : t('Riješeno', 'Resolved')}</button>
                                ) : null}
                                {r.resolution_note ? <div className="muted" style={{ fontSize: '0.72rem' }}>{r.resolution_note}</div> : null}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {data.items.length === 0 ? <p className="muted">{t('Nema redaka za pregled.', 'Nothing to review.')}</p> : null}
        </div>
    );
}

function SuppliersTab({ c, reloadMeta }: { c: Ctx; reloadMeta: () => void }) {
    const { t, eur, meta } = c;
    const [form, setForm] = useState<{ id?: number; name: string; supplier_kind: string; default_credit_limit_eur: string; default_days_to_pay: string; is_active: boolean; notes: string } | null>(null);
    const save = async () => {
        if (!form) return;
        const body = { name: form.name, supplier_kind: form.supplier_kind, default_credit_limit_eur: numOrNull(form.default_credit_limit_eur), default_days_to_pay: Number(form.default_days_to_pay || 0), is_active: form.is_active, notes: form.notes || null };
        try {
            if (form.id) await mutate(`/import-orders/suppliers/${form.id}`, c.token, 'PATCH', body);
            else await mutate('/import-orders/suppliers', c.token, 'POST', body);
            c.notify(t('Dobavljač spremljen.', 'Supplier saved.'));
            setForm(null);
            reloadMeta();
        } catch (e) {
            c.onError(e);
        }
    };
    return (
        <div>
            {meta.can_admin ? (
                <div style={row}>
                    <button type="button" className="compact" onClick={() => setForm({ name: '', supplier_kind: 'goods', default_credit_limit_eur: '', default_days_to_pay: '8', is_active: true, notes: '' })}>{t('Novi dobavljač', 'New supplier')}</button>
                </div>
            ) : null}
            {form ? (
                <div style={box}>
                    <div style={row}>
                        <Field label={t('Naziv', 'Name')}><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                        <Field label={t('Vrsta', 'Kind')}>
                            <select value={form.supplier_kind} onChange={(e) => setForm({ ...form, supplier_kind: e.target.value })}>
                                <option value="goods">{t('Roba', 'Goods')}</option>
                                <option value="carrier">{t('Prijevoznik', 'Carrier')}</option>
                            </select>
                        </Field>
                        <Field label={t('Kreditni limit EUR', 'Credit limit EUR')}><input value={form.default_credit_limit_eur} onChange={(e) => setForm({ ...form, default_credit_limit_eur: e.target.value })} /></Field>
                        <Field label={t('Dana do plaćanja', 'Days to pay')}><input value={form.default_days_to_pay} onChange={(e) => setForm({ ...form, default_days_to_pay: e.target.value })} style={{ width: 70 }} /></Field>
                        <label style={{ fontSize: '0.8rem' }}><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> {t('aktivan', 'active')}</label>
                        <Field label={t('Napomena', 'Notes')}><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
                        <button type="button" className="compact" disabled={!form.name} onClick={save}>{t('Spremi', 'Save')}</button>
                        <button type="button" className="secondary compact" onClick={() => setForm(null)}>{t('Odustani', 'Cancel')}</button>
                    </div>
                </div>
            ) : null}
            <table style={table}>
                <thead><tr><th style={th}>{t('Naziv', 'Name')}</th><th style={th}>{t('Vrsta', 'Kind')}</th><th style={thn}>{t('Kreditni limit', 'Credit limit')}</th><th style={thn}>{t('Dana do plaćanja', 'Days to pay')}</th><th style={th}>{t('Aktivan', 'Active')}</th><th style={th} /></tr></thead>
                <tbody>
                    {meta.suppliers.map((s) => (
                        <tr key={s.id}>
                            <td style={td}>{s.name}</td><td style={td}>{s.kind === 'carrier' ? t('Prijevoznik', 'Carrier') : t('Roba', 'Goods')}</td>
                            <td style={num}>{s.default_credit_limit_eur !== null ? eur(s.default_credit_limit_eur) : '-'}</td><td style={num}>{s.default_days_to_pay}</td>
                            <td style={td}>{s.is_active ? t('da', 'yes') : t('ne', 'no')}</td>
                            <td style={td}>
                                {meta.can_admin ? (
                                    <button type="button" className="secondary compact" onClick={() => setForm({ id: s.id, name: s.name, supplier_kind: s.kind, default_credit_limit_eur: s.default_credit_limit_eur !== null ? String(s.default_credit_limit_eur) : '', default_days_to_pay: String(s.default_days_to_pay), is_active: s.is_active, notes: s.notes ?? '' })}>
                                        {t('Uredi', 'Edit')}
                                    </button>
                                ) : null}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
